import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

interface PaymentRecord {
  id: string;
  user_id: string;
  payment_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_type: string;
  plan_type: string | null;
  credits: number;
  extra_messages: number;
  description: string | null;
  created_at: string;
}

const statusLabels: Record<string, string> = {
  approved: 'Aprovado',
  pending: 'Pendente',
  rejected: 'Rejeitado',
  refunded: 'Reembolsado',
  cancelled: 'Cancelado',
  in_process: 'Em Processo',
  charged_back: 'Chargeback',
};

const statusColors: Record<string, string> = {
  approved: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  pending: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400',
  rejected: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400',
  refunded: 'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400',
  cancelled: 'bg-foreground-100 dark:bg-foreground-200 text-foreground-500',
  in_process: 'bg-secondary-100 dark:bg-secondary-500/15 text-secondary-700 dark:text-secondary-400',
  charged_back: 'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400',
};

const typeLabels: Record<string, string> = {
  plan: 'Plano',
  credits: 'Créditos',
  excess: 'Excedente',
  setup: 'Implantação',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

export default function InvoicesPage() {
  const { profile } = useAuth();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!profile?.id) {
          // Use mock data when no profile (pre-auth or fallback)
          setPayments([
            {
              id: 'mock-1',
              user_id: 'xxx',
              payment_id: '1234567890',
              amount: 297.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'plan',
              plan_type: 'professional',
              credits: 0,
              extra_messages: 0,
              description: 'Plano Professional — Mensal',
              created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
            },
            {
              id: 'mock-2',
              user_id: 'xxx',
              payment_id: '1234567891',
              amount: 60.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'credits',
              plan_type: null,
              credits: 10000,
              extra_messages: 0,
              description: 'Pacote 10.000 créditos',
              created_at: new Date(Date.now() - 86400000 * 8).toISOString(),
            },
            {
              id: 'mock-3',
              user_id: 'xxx',
              payment_id: '1234567892',
              amount: 150.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'excess',
              plan_type: null,
              credits: 0,
              extra_messages: 30000,
              description: 'Excedente: +30.000 mensagens',
              created_at: new Date(Date.now() - 86400000 * 20).toISOString(),
            },
            {
              id: 'mock-4',
              user_id: 'xxx',
              payment_id: '1234567893',
              amount: 97.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'plan',
              plan_type: 'starter',
              credits: 0,
              extra_messages: 0,
              description: 'Plano Starter — Primeira assinatura',
              created_at: new Date(Date.now() - 86400000 * 35).toISOString(),
            },
            {
              id: 'mock-5',
              user_id: 'xxx',
              payment_id: '1234567894',
              amount: 250.00,
              currency: 'BRL',
              status: 'pending',
              payment_type: 'credits',
              plan_type: null,
              credits: 50000,
              extra_messages: 0,
              description: 'Pacote 50.000 créditos',
              created_at: new Date(Date.now() - 3600000 * 4).toISOString(),
            },
            {
              id: 'mock-6',
              user_id: 'xxx',
              payment_id: '1234567895',
              amount: 997.00,
              currency: 'BRL',
              status: 'refunded',
              payment_type: 'setup',
              plan_type: null,
              credits: 0,
              extra_messages: 0,
              description: 'Implantação Completa — Reembolsado',
              created_at: new Date(Date.now() - 86400000 * 60).toISOString(),
            },
            {
              id: 'mock-7',
              user_id: 'xxx',
              payment_id: '1234567896',
              amount: 297.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'plan',
              plan_type: 'professional',
              credits: 0,
              extra_messages: 0,
              description: 'Plano Professional — Renovação',
              created_at: new Date(Date.now() - 86400000 * 33).toISOString(),
            },
          ]);
          return;
        }

        const query = supabase
          .from('payments')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(50);

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        if (data && data.length > 0) {
          setPayments(data as PaymentRecord[]);
        } else {
          // No real data yet, show mock demo
          setPayments([
            {
              id: 'demo-1',
              user_id: profile.id,
              payment_id: 'DEMO12345',
              amount: 97.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'plan',
              plan_type: 'starter',
              credits: 0,
              extra_messages: 0,
              description: 'Plano Starter — Primeira assinatura',
              created_at: new Date(Date.now() - 86400000 * 5).toISOString(),
            },
            {
              id: 'demo-2',
              user_id: profile.id,
              payment_id: 'DEMO12346',
              amount: 60.00,
              currency: 'BRL',
              status: 'approved',
              payment_type: 'credits',
              plan_type: null,
              credits: 10000,
              extra_messages: 0,
              description: 'Pacote 10.000 mensagens extras',
              created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
            },
          ]);
        }
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('Nao foi possivel carregar o historico. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [profile?.id]);

  const filteredPayments = filter === 'all'
    ? payments
    : payments.filter((p) => p.payment_type === filter);

  // Stats
  const totalApproved = payments
    .filter((p) => p.status === 'approved')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const totalCredits = payments
    .filter((p) => p.status === 'approved' && p.credits > 0)
    .reduce((sum, p) => sum + p.credits, 0);

  return (
    <div className="max-w-5xl mx-auto space-y-7">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
            Faturas e Historico
          </h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Todas as suas transacoes e pagamentos do MercadoPago
          </p>
        </div>
        <Link
          to="/subscription"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Novo plano ou creditos
        </Link>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-foreground-500">Total aprovado</p>
              <p className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">
                {formatCurrency(totalApproved)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
              <i className="ri-exchange-funds-line text-xl text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-foreground-500">Transacoes</p>
              <p className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">
                {payments.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center">
              <i className="ri-message-2-line text-xl text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <p className="text-xs text-foreground-500">Creditos comprados</p>
              <p className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">
                {totalCredits.toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-foreground-500 mr-1">Filtrar:</span>
        {[
          { key: 'all', label: 'Todos' },
          { key: 'plan', label: 'Planos' },
          { key: 'credits', label: 'Creditos' },
          { key: 'excess', label: 'Excedentes' },
          { key: 'setup', label: 'Implantacao' },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
              filter === f.key
                ? 'bg-primary-500 text-white'
                : 'bg-background-50 dark:bg-background-200 text-foreground-600 dark:text-foreground-400 hover:bg-background-100 dark:hover:bg-background-300 border border-background-200 dark:border-background-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-7 h-7 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-16 px-6">
            <div className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-500" />
            </div>
            <p className="text-sm text-foreground-700 dark:text-foreground-300 mb-3">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-5 py-2 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-14 h-14 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-receipt-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1">
              Nenhuma transacao encontrada
            </p>
            <p className="text-xs text-foreground-500 mb-5">
              {filter !== 'all' ? 'Tente mudar o filtro ou ver todos os tipos.' : 'Seus pagamentos aparecerao aqui apos a primeira compra.'}
            </p>
            <Link
              to="/subscription"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors whitespace-nowrap"
            >
              <i className="ri-vip-crown-line" />
              Ver planos
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 dark:border-background-200">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Descricao</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Tipo</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Valor</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Data</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">ID Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment, idx) => (
                  <tr
                    key={payment.id}
                    className={`border-b border-background-50 dark:border-background-200 hover:bg-background-50/50 dark:hover:bg-background-200/50 transition-colors ${
                      idx === 0 ? '' : ''
                    }`}
                  >
                    <td className="px-5 py-4">
                      <p className="text-foreground-900 dark:text-foreground-100 font-medium text-xs">
                        {payment.description || `${typeLabels[payment.payment_type] || payment.payment_type}${payment.plan_type ? ` (${payment.plan_type})` : ''}`}
                      </p>
                      {payment.credits > 0 && (
                        <p className="text-[10px] text-foreground-400 mt-0.5">
                          +{payment.credits.toLocaleString('pt-BR')} creditos
                        </p>
                      )}
                      {payment.extra_messages > 0 && (
                        <p className="text-[10px] text-foreground-400 mt-0.5">
                          +{payment.extra_messages.toLocaleString('pt-BR')} mensagens
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-secondary-50 dark:bg-secondary-500/10 text-secondary-700 dark:text-secondary-400">
                        {typeLabels[payment.payment_type] || payment.payment_type}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-semibold text-foreground-900 dark:text-foreground-100 tabular-nums text-xs">
                      {formatCurrency(Number(payment.amount))}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-medium ${statusColors[payment.status] || 'bg-foreground-100 dark:bg-foreground-200 text-foreground-500'}`}>
                        {statusLabels[payment.status] || payment.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right text-xs text-foreground-500 whitespace-nowrap">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="text-[10px] text-foreground-400 font-mono">
                        {payment.payment_id.slice(0, 12)}...
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MercadoPago trust badge */}
      <div className="flex items-center justify-center gap-2 text-[10px] text-foreground-400">
        <i className="ri-shield-check-line text-sm" />
        <span>Pagamentos processados com seguranca pelo MercadoPago</span>
      </div>
    </div>
  );
}